import {Card, IconButton} from 'platform/components';
import {Box, Heading, HStack, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNil} from 'ramda';

import {DrillDownLevelName, selectTenant, SourcingVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DEFAULT_COUNTRY, DrillDown} from '@dms/shared';

import {buildArray} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../../constants/DEFAULT_CURRENCY_CODE';
import {useIsSourcingCardViewEnforced} from '../../../../hooks/useIsSourcingCardViewEnforced';
import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {isUnifiedSourcingEnabled} from '../../../../utils/isUnifiedSourcingEnabled';
import {useDrillDownStockUrlLoader} from '../hooks/useDrillDownStockUrlLoader';
import {getDrillDownSelectedRow} from '../utils/getDrillDownSelectedRow';

interface FullScreenDetailLayoutProps {
  vehicle?: SourcingVehicleDetailResponseBody;
  onClose?: () => void;
}

export function FullScreenDetailLayout({vehicle, onClose}: FullScreenDetailLayoutProps) {
  const {data: {country: tenantCountry} = {country: DEFAULT_COUNTRY}} = useSelector(selectTenant);
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code ?? tenantCountry;
  const isCardViewEnforced = useIsSourcingCardViewEnforced();
  const hasUnifiedSurfaces = isUnifiedSourcingEnabled();

  const hiddenLevels = buildArray<DrillDownLevelName>([])
    .when(isNil(vehicle?.sourcingVehicle.make), 'make')
    .when(isNil(vehicle?.sourcingVehicle.modelFamilyGroup), 'model_family_group')
    .when(isNil(vehicle?.sourcingVehicle.model), 'model');
  const drillDownCurrency = currency ?? DEFAULT_CURRENCY_CODE;
  const getStockUrlLoader = useDrillDownStockUrlLoader({
    vehicle,
    hiddenLevels,
    countryCode: country,
    currency: drillDownCurrency,
  });

  return (
    <Box
      width="100%"
      height="100dvh"
      backgroundColor={hasUnifiedSurfaces ? 'palettes.neutral.20.100' : 'palettes.neutral.30.100'}
    >
      <VStack>
        <Box paddingHorizontal={6} paddingVertical={3} backgroundColor="palettes.white.10.100">
          <HStack align="center" justify="space-between">
            <Heading size={1}>{i18n.t('general.labels.drillDown')}</Heading>
            <IconButton
              data-testid="close-fullscreen-detail-layout"
              onClick={() => onClose?.()}
              icon="navigation/close"
            />
          </HStack>
        </Box>
        {/* Scoped to the card view: a non-visible overflow-x makes overflow-y compute to auto too, so
            this would clip vertically on breakpoints where DrillDown already fits. */}
        <Box
          flex={1}
          paddingVertical={4}
          paddingHorizontal={2}
          overflowX={isCardViewEnforced ? 'auto' : undefined}
        >
          <Card>
            <DrillDown
              hasUnifiedSurfaces={hasUnifiedSurfaces}
              regions={[country]}
              currency={drillDownCurrency}
              hiddenLevels={hiddenLevels}
              selectedRow={getDrillDownSelectedRow(vehicle)}
              scrollToSelectedRow
              getStockUrlLoader={getStockUrlLoader}
              data-testid={testIds.sourcing.vehicleDetail('fullScreenDetailLayout-drillDown')}
            />
          </Card>
        </Box>
      </VStack>
    </Box>
  );
}
